/**
 * The mosquito controls a page adds to its scene: the right-click menu, dragging to the jar or to
 * a person, and what each of those asks the server to do.
 *
 * Returns handlers for the page's wrapper and an overlay to render beside the scene. Nothing is
 * moved on screen by hand: the server's answer — and the broadcast everybody else receives — is
 * what moves the mosquito, so every viewer sees the same thing.
 */

import { useCallback, useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { BugMenu, type BugAction } from '../components/BugMenu'
import { BugCreature } from './Figures'
import { seedFrom } from './layout'
import { useBugInteractions, type BugDrop, type BugMenuRequest } from './useBugInteractions'
import type { BugHuntStore } from './store'

export function useBugControls({
  store,
  enabled,
  onOpenBug,
}: {
  store: BugHuntStore
  enabled: boolean
  onOpenBug: (taskId: string) => void
}): { handlers: ReturnType<typeof useBugInteractions>['handlers']; overlay: ReactNode } {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [menu, setMenu] = useState<BugMenuRequest | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const transition = useMutation({
    mutationFn: ({ taskId, action, developerId }: { taskId: string; action: BugAction; developerId?: string }) =>
      api.bugHunt.transition(taskId, action, developerId),
    onSuccess: () => {
      setNote(null)
      for (const key of ['bug-hunt', 'room-activity', 'dashboard']) void queryClient.invalidateQueries({ queryKey: [key] })
    },
    onError: (error) => {
      setNote(error instanceof ApiError && error.code === 'unassigned' ? t('bugMenu.error.unassigned') : t('bugMenu.error.generic'))
      window.setTimeout(() => setNote(null), 5000)
    },
  })

  const onDrop = useCallback(
    (taskId: string, target: BugDrop) => {
      const bug = store.snapshot().bugs.get(taskId)
      if (!bug) return
      if (target.kind === 'jar') {
        if (bug.state !== 'Waiting' || bug.developerId) transition.mutate({ taskId, action: 'wait' })
      } else if (target.id !== bug.developerId) {
        transition.mutate({ taskId, action: 'assign', developerId: target.id })
      }
    },
    [store, transition],
  )

  const { handlers, ghost } = useBugInteractions({ enabled, onMenu: setMenu, onDrop })

  const snapshot = store.snapshot()
  const menuBug = menu ? snapshot.bugs.get(menu.taskId) : undefined
  const ghostBug = ghost ? snapshot.bugs.get(ghost.taskId) : undefined

  const ghostLabel = !ghost
    ? ''
    : ghost.over?.kind === 'jar'
      ? t('bugMenu.dropJar')
      : ghost.over?.kind === 'developer'
        ? t('bugMenu.dropPerson', { name: snapshot.developers.get(ghost.over.id)?.name ?? '' })
        : t('bugMenu.dragHint')

  const overlay = (
    <>
      {menu && menuBug && (
        <BugMenu
          bug={{ taskId: menuBug.taskId, externalId: menuBug.externalId, title: menuBug.title, state: menuBug.state, developerId: menuBug.developerId }}
          x={menu.x}
          y={menu.y}
          developers={[...snapshot.developers.values()].map((d) => ({ id: d.id, name: d.name }))}
          onAction={(action, developerId) => {
            transition.mutate({ taskId: menuBug.taskId, action, developerId })
            setMenu(null)
          }}
          onOpen={() => {
            onOpenBug(menuBug.taskId)
            setMenu(null)
          }}
          onClose={() => setMenu(null)}
        />
      )}

      {ghost && ghostBug && (
        <div className={`bug-ghost${ghost.over ? ' bug-ghost--over' : ''}`} style={{ left: ghost.x, top: ghost.y }} aria-hidden="true">
          <svg viewBox="-40 -40 80 80" width="46" height="46">
            <BugCreature priority={ghostBug.priority} seed={seedFrom(ghostBug.taskId)} weakened={false} still={false} />
          </svg>
          <span className="bug-ghost__label">
            <span className="mono bold">{ghostBug.externalId}</span> · {ghostLabel}
          </span>
        </div>
      )}

      {note && <div className="bug-note" role="alert">{note}</div>}
    </>
  )

  return { handlers, overlay }
}
